import { z } from "zod"

const relativeDateRangeSchema = z.object({
    type: z.literal("relative"),
    days: z.number(),
})

const monthDateRangeSchema = z.object({
    type: z.literal("month"),
    year: z.union([z.number(), z.literal("current")]),
    month: z.union([z.number(), z.literal("current")]),
})

const yearToDateRangeSchema = z.object({
    type: z.literal("year_to_date"),
    year: z.union([z.number(), z.literal("current")]),
})

const customDateRangeSchema = z.object({
    type: z.literal("custom"),
    start: z.string(),
    end: z.string(),
})

const lastCalendarMonthDateRangeSchema = z.object({
    type: z.literal("last_calendar_month"),
})

export const reportDateRangeSchema = z.discriminatedUnion("type", [
    relativeDateRangeSchema,
    monthDateRangeSchema,
    yearToDateRangeSchema,
    customDateRangeSchema,
    lastCalendarMonthDateRangeSchema,
])

export type ReportDateRange = z.infer<typeof reportDateRangeSchema>

export const reportFiltersSchema = z.object({
    pilot_ids: z.array(z.guid()).optional(),
    aircraft_ids: z.array(z.uuid()).optional(),
    min_flight_time: z.number().optional(),
    min_distance: z.number().optional(),
})

export type ReportFilters = z.infer<typeof reportFiltersSchema>

const reportUnitsSchema = z.object({
    date: z.enum(["MM/DD/YYYY", "DD/MM/YYYY"]).optional(),
    time: z.enum(["12h", "24h"]).optional(),
    distance: z.enum(["feet", "miles", "meters", "kilometers"]).optional(),
    altitude: z.enum(["feet", "meters"]).optional(),
    speed: z.enum(["mph", "kph"]).optional(),
    temperature: z.enum(["fahrenheit", "celsius"]).optional(),
})

export const flightColumnIdSchema = z.enum([
    "id",
    "location",
    "takeoff_latitude",
    "takeoff_longitude",
    "pilot_in_command",
    "duration",
    "checklist_status",
    "num_images",
    "num_videos",
    "notes",
    "takeoff_battery_percent",
    "landing_battery_percent",
    "flights_at_location",
])

export const droneColumnIdSchema = z.enum([
    "nickname",
    "manufacturer",
    "model",
    "serial_number",
    "registration_number",
    "firmware_version",
])

export const metricsColumnIdSchema = z.enum([
    "avg_altitude",
    "max_altitude",
    "avg_speed",
    "max_speed",
])

export type FlightColumnId = z.infer<typeof flightColumnIdSchema>
export type DroneColumnId = z.infer<typeof droneColumnIdSchema>
export type MetricsColumnId = z.infer<typeof metricsColumnIdSchema>

export const reportSortFieldSchema = z.enum([
    "started_at",
    "pilot_in_command",
    "duration",
    "model",
    "avg_altitude",
    "max_altitude",
    "avg_speed",
    "max_speed",
])

export type ReportSortField = z.infer<typeof reportSortFieldSchema>

const reportSortSchema = z.object({
    field: reportSortFieldSchema,
    order: z.enum(["asc", "desc"]),
})

export const reportFormatConfigSchema = z.object({
    columns: z
        .object({
            flight: z.array(flightColumnIdSchema).optional(),
            drones: z.array(droneColumnIdSchema).optional(),
            metrics: z.array(metricsColumnIdSchema).optional(),
            customFields: z.boolean().optional(),
        })
        .optional(),
    units: reportUnitsSchema.optional(),
    sort: reportSortSchema.optional(),
})

export type ReportFormatConfig = z.infer<typeof reportFormatConfigSchema>

export const reportScheduleSchema = z.object({
    frequency: z.enum(["once", "weekly", "monthly", "annually"]),
    send_time: z.string(),
    start_date: z.string(),
    timezone: z.string().optional(),
})

export type ReportSchedule = z.infer<typeof reportScheduleSchema>

const emailNotificationSchema = z.object({
    recipients: z.object({
        addresses: z.array(z.string()),
    }),
})

export const reportNotificationConfigSchema = z.object({
    active: z.boolean(),
    email: emailNotificationSchema,
})

export type ReportNotificationConfig = z.infer<typeof reportNotificationConfigSchema>

export const reportTemplateRecordSchema = z.object({
    id: z.uuid(),
    org_id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    report_type: z.enum(["flight_report", "coa_report"]),
    date_range: reportDateRangeSchema,
    filters: reportFiltersSchema,
    format_config: reportFormatConfigSchema,
    schedule: reportScheduleSchema.nullable(),
    notification_config: reportNotificationConfigSchema.nullable(),
    last_run_at: z.string().nullable(),
    created_at: z.string(),
    created_by: z.guid(),
    updated_at: z.string().nullable(),
    updated_by: z.guid().nullable(),
    deleted_at: z.string().nullable(),
})

export const reportTemplateSchema = reportTemplateRecordSchema.transform((r) => ({
    id: r.id,
    orgId: r.org_id,
    name: r.name,
    description: r.description,
    reportType: r.report_type,
    dateRange: r.date_range,
    filters: r.filters,
    formatConfig: r.format_config,
    schedule: r.schedule,
    notificationConfig: r.notification_config,
    lastRunAt: r.last_run_at,
    createdAt: r.created_at,
    createdBy: r.created_by,
    updatedAt: r.updated_at,
    updatedBy: r.updated_by,
    deletedAt: r.deleted_at,
}))

export const creatableReportTemplateSchema = reportTemplateRecordSchema.omit({
    id: true,
    org_id: true,
    last_run_at: true,
    created_at: true,
    created_by: true,
    updated_at: true,
    updated_by: true,
    deleted_at: true,
})

export const updatableReportTemplateSchema = reportTemplateRecordSchema.pick({
    name: true,
    description: true,
    report_type: true,
    date_range: true,
    filters: true,
    format_config: true,
    schedule: true,
    notification_config: true,
})

export type ReportTemplateRecord = z.infer<typeof reportTemplateRecordSchema>
export type ReportTemplate = z.infer<typeof reportTemplateSchema>
export type CreatableReportTemplate = z.infer<typeof creatableReportTemplateSchema>
export type UpdatableReportTemplate = z.infer<typeof updatableReportTemplateSchema>

export type ReportRenderConfig = Pick<
    ReportTemplate,
    "orgId" | "name" | "reportType" | "dateRange" | "filters" | "formatConfig"
>

export const templateToRenderConfig = (template: ReportTemplate): ReportRenderConfig => ({
    orgId: template.orgId,
    name: template.name,
    reportType: template.reportType,
    dateRange: template.dateRange,
    filters: template.filters,
    formatConfig: template.formatConfig,
})

export const creatableToRenderConfig = (
    orgId: string,
    creatable: CreatableReportTemplate
): ReportRenderConfig => ({
    orgId,
    name: creatable.name,
    reportType: creatable.report_type,
    dateRange: creatable.date_range,
    filters: creatable.filters,
    formatConfig: creatable.format_config,
})
